// Ngày lễ và nghỉ phép: hàm thuần, không gọi API. Chuyển từ VUONGDATWOOD_V2/src/features/lich-ngay-le/calculation.ts
// và phep-nam/calculation.ts. Khác V2: số phép đã dùng tính lại từ các đơn đã duyệt thay vì cộng dồn vào một
// cột, vì AppSheet không có transaction — ghi đơn xong mà hỏng lúc ghi số dư là hai bên lệch nhau mãi.
import { normalizeTeamKey } from './templateResolver';

export const HOLIDAY_TYPES = {
    HUONG_LUONG: 'Hưởng lương',
    KHONG_HUONG_LUONG: 'Không hưởng lương',
};

export const HOLIDAY_SCOPES = {
    TOAN_CONG_TY: 'Toàn công ty',
    TO: 'Theo tổ',
    NHAN_SU: 'Theo nhân sự',
};

// paid: tính vào ngày phép hưởng lương (PN) của kỳ; deduct: trừ vào quỹ phép năm.
export const LEAVE_TYPES = {
    PHEP_NAM: { label: 'Phép năm', paid: true, deduct: true },
    VIEC_RIENG: { label: 'Việc riêng có lương (cưới, tang...)', paid: true, deduct: false },
    KHONG_LUONG: { label: 'Nghỉ không lương', paid: false, deduct: false },
};

export const LEAVE_STATUSES = {
    NHAP: { label: 'Nháp', tone: 'gray' },
    CHO_DUYET: { label: 'Chờ duyệt', tone: 'yellow' },
    DA_DUYET: { label: 'Đã duyệt', tone: 'green' },
    TU_CHOI: { label: 'Từ chối', tone: 'red' },
    HUY: { label: 'Đã hủy', tone: 'gray' },
};

// Đơn còn giữ chỗ trên lịch: hai đơn này không được chồng ngày nhau.
const ACTIVE_LEAVE_STATUSES = ['NHAP', 'CHO_DUYET', 'DA_DUYET'];

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export function localDateKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Các ngày làm việc (bỏ Chủ nhật) từ from đến to, tính cả hai đầu. */
export function listWorkingDays(from, to) {
    const result = [];
    const cursor = startOfDay(from);
    const end = startOfDay(to);
    while (cursor <= end) {
        if (cursor.getDay() !== 0) result.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return result;
}

/**
 * Ngày lễ có áp cho người này không. Nhận danh sách tổ vì một người có thể phụ trách nhiều tổ; so theo khóa
 * chuẩn hóa để không lệch vì hoa thường hay khoảng trắng.
 */
export function holidayAppliesToEmployee(holiday, soHd, teams = []) {
    if (holiday.PHAM_VI === 'NHAN_SU') return Boolean(soHd && holiday.NHAN_SU_ID === soHd);
    if (holiday.PHAM_VI === 'TO') {
        const wanted = normalizeTeamKey(holiday.TO);
        return Boolean(wanted) && (Array.isArray(teams) ? teams : [teams]).map(normalizeTeamKey).includes(wanted);
    }
    return true;
}

/** Ngày lễ được dùng để tính lương: còn hiệu lực, đã xác nhận, hưởng lương. */
export function isPaidConfirmedHoliday(holiday) {
    return holiday.HIEU_LUC !== false && holiday.DA_XAC_NHAN && holiday.LOAI_NGAY === 'HUONG_LUONG' && holiday.TU_NGAY && holiday.DEN_NGAY;
}

/**
 * Số ngày lễ hưởng lương của một người trong [from, to]: mỗi ngày lễ phân SO_NGAY_HUONG_LUONG lên các ngày
 * làm việc của nó theo thứ tự (phần lẻ ở ngày cuối), hai ngày lễ chồng nhau không đếm một ngày hai lần.
 */
export function countPaidHolidayDays(from, to, holidays, soHd, teams) {
    const days = new Map();
    for (const holiday of holidays) {
        if (!isPaidConfirmedHoliday(holiday) || !holidayAppliesToEmployee(holiday, soHd, teams)) continue;
        let remaining = Math.max(0, Number(holiday.SO_NGAY_HUONG_LUONG) || 0);
        // Phân bổ trên cả khoảng của ngày lễ rồi mới cắt theo kỳ, để ngày lễ vắt qua hai tháng không bị đếm dồn.
        for (const date of listWorkingDays(holiday.TU_NGAY, holiday.DEN_NGAY)) {
            if (remaining <= 0) break;
            const part = Math.min(1, remaining);
            remaining -= part;
            if (date < startOfDay(from) || date > startOfDay(to)) continue;
            const key = localDateKey(date);
            days.set(key, Math.max(days.get(key) ?? 0, part));
        }
    }
    return Array.from(days.values()).reduce((sum, value) => sum + value, 0);
}

/** Các ngày (khóa YYYY-MM-DD) là ngày lễ hưởng lương của người này trong khoảng: đơn phép không trừ những ngày này. */
export function holidayDateKeys(from, to, holidays, soHd, teams) {
    const keys = new Set();
    for (const holiday of holidays) {
        if (!isPaidConfirmedHoliday(holiday) || !holidayAppliesToEmployee(holiday, soHd, teams)) continue;
        const start = new Date(Math.max(startOfDay(from).getTime(), startOfDay(holiday.TU_NGAY).getTime()));
        const end = new Date(Math.min(startOfDay(to).getTime(), startOfDay(holiday.DEN_NGAY).getTime()));
        for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) keys.add(localDateKey(cursor));
    }
    return keys;
}

/**
 * Chi tiết từng ngày của một đơn nghỉ: Chủ nhật và ngày lễ hưởng lương không tính; nghỉ nửa ngày chỉ khi
 * đơn một ngày. Trả về { days: [{ ngay, cn, le, soNgay }], total }.
 */
export function buildLeaveDays(from, to, holidayKeys = new Set(), halfDay = false) {
    const days = [];
    for (const cursor = startOfDay(from); cursor <= startOfDay(to); cursor.setDate(cursor.getDate() + 1)) {
        const key = localDateKey(cursor);
        const cn = cursor.getDay() === 0;
        const le = holidayKeys.has(key);
        days.push({ ngay: key, cn, le, soNgay: cn || le ? 0 : 1 });
    }
    if (halfDay && days.length === 1 && days[0].soNgay) days[0].soNgay = 0.5;
    return { days, total: days.reduce((sum, day) => sum + day.soNgay, 0) };
}

const parseKey = (key) => { const [year, month, day] = key.split('-').map(Number); return new Date(year, month - 1, day); };

/** Ngày phép hưởng lương (PN) theo từng người trong [from, to]: đơn đã duyệt thuộc loại có lương, chỉ phần ngày nằm trong kỳ. */
export function paidLeaveDaysByEmployee(requests, from, to) {
    const result = new Map();
    for (const request of requests) {
        if (request.TRANG_THAI !== 'DA_DUYET' || !LEAVE_TYPES[request.LOAI_PHEP]?.paid) continue;
        const inRange = (request.CHI_TIET_NGAY ?? []).filter((day) => {
            const date = parseKey(day.ngay);
            return date >= startOfDay(from) && date <= startOfDay(to);
        }).reduce((sum, day) => sum + (Number(day.soNgay) || 0), 0);
        if (inRange) result.set(request.NHAN_SU_ID, (result.get(request.NHAN_SU_ID) ?? 0) + inRange);
    }
    return result;
}

/** Số phép năm đã dùng của một người trong năm: tổng ngày các đơn phép năm đã duyệt. */
export function usedAnnualLeave(requests, soHd, year) {
    return requests
        .filter((request) => request.NHAN_SU_ID === soHd && request.NAM === year && request.TRANG_THAI === 'DA_DUYET' && LEAVE_TYPES[request.LOAI_PHEP]?.deduct)
        .reduce((sum, request) => sum + (Number(request.SO_NGAY) || 0), 0);
}

export function calculateLeaveBalance({ standard = 0, carryOver = 0, adjustment = 0, used = 0, pending = 0 }) {
    const entitled = Math.max(0, (Number(standard) || 0) + (Number(carryOver) || 0) + (Number(adjustment) || 0));
    return { entitled, used, pending, remaining: entitled - used };
}

/** Sổ phép của một năm kèm số đã dùng, đang chờ duyệt và còn lại. */
export function summarizeLeaveBalance(balance, requests) {
    const pending = requests
        .filter((request) => request.NHAN_SU_ID === balance.NHAN_SU_ID && request.NAM === balance.NAM && request.TRANG_THAI === 'CHO_DUYET' && LEAVE_TYPES[request.LOAI_PHEP]?.deduct)
        .reduce((sum, request) => sum + (Number(request.SO_NGAY) || 0), 0);
    return {
        ...balance,
        ...calculateLeaveBalance({
            standard: balance.PHEP_CHUAN, carryOver: balance.PHEP_TON_NAM_TRUOC, adjustment: balance.PHEP_DIEU_CHINH,
            used: usedAnnualLeave(requests, balance.NHAN_SU_ID, balance.NAM), pending,
        }),
    };
}

const overlaps = (left, right) => startOfDay(left.TU_NGAY) <= startOfDay(right.DEN_NGAY) && startOfDay(left.DEN_NGAY) >= startOfDay(right.TU_NGAY);

/** Ngày lễ cùng phạm vi, cùng đối tượng đang chồng ngày với ngày lễ này (bỏ qua chính nó). */
export function findHolidayOverlap(holidays, candidate) {
    const target = (holiday) => (holiday.PHAM_VI === 'TO' ? normalizeTeamKey(holiday.TO) : holiday.PHAM_VI === 'NHAN_SU' ? holiday.NHAN_SU_ID : '');
    return holidays.find((holiday) => (!candidate.ID || holiday.ID !== candidate.ID) && holiday.HIEU_LUC !== false
        && holiday.PHAM_VI === candidate.PHAM_VI && target(holiday) === target(candidate) && overlaps(holiday, candidate)) ?? null;
}

export function validateHolidayInput(holiday) {
    const errors = [];
    if (!String(holiday.TEN_NGAY_LE ?? '').trim()) errors.push('Nhập tên ngày lễ.');
    if (!holiday.TU_NGAY) errors.push('Chọn ngày bắt đầu.');
    if (holiday.TU_NGAY && holiday.DEN_NGAY && startOfDay(holiday.DEN_NGAY) < startOfDay(holiday.TU_NGAY)) errors.push('Ngày kết thúc phải từ ngày bắt đầu trở đi.');
    if (!(Number(holiday.SO_NGAY_HUONG_LUONG) >= 0)) errors.push('Số ngày hưởng lương phải là số không âm.');
    if (holiday.PHAM_VI === 'TO' && !String(holiday.TO ?? '').trim()) errors.push('Chọn tổ áp dụng.');
    if (holiday.PHAM_VI === 'NHAN_SU' && !holiday.NHAN_SU_ID) errors.push('Chọn nhân sự áp dụng.');
    return errors;
}

/** Đơn còn hiệu lực của cùng người chồng ngày với đơn này. */
export function findLeaveOverlap(requests, candidate) {
    return requests.find((request) => (!candidate.MA_DON || request.MA_DON !== candidate.MA_DON) && request.NHAN_SU_ID === candidate.NHAN_SU_ID
        && ACTIVE_LEAVE_STATUSES.includes(request.TRANG_THAI) && overlaps(request, candidate)) ?? null;
}

export function validateLeaveRequest(request) {
    const errors = [];
    if (!request.NHAN_SU_ID) errors.push('Chọn nhân sự.');
    if (!LEAVE_TYPES[request.LOAI_PHEP]) errors.push('Chọn loại nghỉ.');
    if (!request.TU_NGAY || !request.DEN_NGAY) errors.push('Chọn ngày nghỉ.');
    else {
        if (startOfDay(request.DEN_NGAY) < startOfDay(request.TU_NGAY)) errors.push('Ngày kết thúc phải từ ngày bắt đầu trở đi.');
        if (request.TU_NGAY.getFullYear() !== request.DEN_NGAY.getFullYear()) errors.push('Đơn nghỉ phải nằm trong cùng một năm (tách thành hai đơn).');
    }
    return errors;
}

/**
 * Lịch năm sau từ năm trước (giữ ngày/tháng, chờ xác nhận lại); bỏ dòng chồng với lịch đã có của năm đích.
 * Ngày lễ âm lịch (Tết, Giỗ tổ) đổi ngày mỗi năm nên vẫn phải sửa tay sau khi nhân bản.
 */
export function cloneHolidaysToYear(holidays, sourceYear, targetYear) {
    const shift = (date) => new Date(targetYear, date.getMonth(), date.getDate());
    const source = holidays.filter((holiday) => holiday.HIEU_LUC !== false && holiday.TU_NGAY?.getFullYear() === sourceYear);
    const rows = [];
    let skipped = 0;
    for (const holiday of source) {
        const candidate = {
            ...holiday,
            ID: null,
            MA_NGAY_LE: String(holiday.MA_NGAY_LE || '').replace(new RegExp(String(sourceYear), 'g'), String(targetYear)),
            TU_NGAY: shift(holiday.TU_NGAY),
            DEN_NGAY: shift(holiday.DEN_NGAY ?? holiday.TU_NGAY),
            DA_XAC_NHAN: false,
            NGUOI_XAC_NHAN: '',
            NGAY_XAC_NHAN: null,
            NGUON: `NHAN_BAN_${sourceYear}`,
        };
        if (findHolidayOverlap([...holidays, ...rows], candidate)) skipped += 1;
        else rows.push(candidate);
    }
    return { rows, skipped };
}

/** Các ngày lễ dương lịch cố định theo Bộ luật Lao động; Tết âm lịch và Giỗ tổ đổi ngày mỗi năm nên nhập tay. */
export function solarHolidaysOfYear(year) {
    const day = (month, date) => new Date(year, month - 1, date);
    return [
        { MA_NGAY_LE: `TET_DL_${year}`, TEN_NGAY_LE: 'Tết Dương lịch', TU_NGAY: day(1, 1), DEN_NGAY: day(1, 1), SO_NGAY_HUONG_LUONG: 1 },
        { MA_NGAY_LE: `30_4_${year}`, TEN_NGAY_LE: 'Ngày Chiến thắng 30/4', TU_NGAY: day(4, 30), DEN_NGAY: day(4, 30), SO_NGAY_HUONG_LUONG: 1 },
        { MA_NGAY_LE: `1_5_${year}`, TEN_NGAY_LE: 'Quốc tế Lao động 1/5', TU_NGAY: day(5, 1), DEN_NGAY: day(5, 1), SO_NGAY_HUONG_LUONG: 1 },
        { MA_NGAY_LE: `QK_${year}`, TEN_NGAY_LE: 'Quốc khánh 2/9 (2 ngày, sửa theo lịch nghỉ năm nay)', TU_NGAY: day(9, 1), DEN_NGAY: day(9, 2), SO_NGAY_HUONG_LUONG: 2 },
    ].map((holiday) => ({ ...holiday, LOAI_NGAY: 'HUONG_LUONG', PHAM_VI: 'TOAN_CONG_TY', TO: '', NHAN_SU_ID: '', HIEU_LUC: true, DA_XAC_NHAN: false, MO_TA: '', NGUON: 'LUAT_LAO_DONG' }));
}
